import React, { useState, useEffect } from "react";
import axios from "axios";
import { server } from "../../server";
import useAuth from "../../useAuth";
import { FaSlidersH, FaSave, FaCheckCircle } from "react-icons/fa";
import { toast } from "react-toastify";

export default function FeeSettingsPage() {
  const [settings, setSettings] = useState({
    receiptPrefix: "REC",
    autoGenerateReceiptNumber: true,
    autoCalculateFine: true,
    enableDiscountApproval: false,
    receiptHeaderNote: "Dee Novo School - Official Fee Receipt",
    receiptFooterNote: "This is a computer generated receipt. Thank you!",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { token } = useAuth();

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${server}/fee-enterprise/settings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data.success && res.data.data) {
          setSettings(res.data.data);
        }
      } catch (error) {
        toast.error("Failed to load Fee Settings");
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchSettings();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const res = await axios.put(`${server}/fee-enterprise/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        toast.success("Fee Settings Updated Successfully!");
      }
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 max-w-[1000px] mx-auto min-h-screen bg-gray-50 dark:bg-slate-950 text-gray-800 dark:text-slate-100 font-sans transition-colors duration-300">
      {/* Compact Top Header Bar */}
      <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-2xl px-5 py-3 mb-4 shadow-xs flex justify-between items-center">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-100 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 rounded-xl shadow-xs">
            <FaSlidersH size={18} />
          </div>
          <div>
            <h1 className="text-lg font-black text-gray-900 dark:text-white leading-tight">Fee System Settings</h1>
            <p className="text-xs text-gray-500 dark:text-slate-400 font-semibold">Receipt numbering prefix, automated late fee rules, and receipt template notes</p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-xs font-bold text-indigo-500">Loading fee settings...</div>
      ) : (
        <form onSubmit={handleSubmit} className="bg-white dark:bg-slate-900 rounded-2xl border border-gray-200 dark:border-slate-800 p-5 shadow-xs space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-extrabold uppercase text-gray-400 dark:text-slate-400 mb-1">
                Receipt Number Prefix *
              </label>
              <input
                type="text"
                required
                value={settings.receiptPrefix}
                onChange={(e) => setSettings({ ...settings, receiptPrefix: e.target.value })}
                className="w-full px-3 py-2 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-xs font-bold text-gray-900 dark:text-white outline-none"
              />
              <span className="text-[10px] font-semibold text-gray-400 mt-1 block">
                Preview: {settings.receiptPrefix}-2026-0001
              </span>
            </div>

            <div className="space-y-2 pt-1">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.autoGenerateReceiptNumber}
                  onChange={(e) => setSettings({ ...settings, autoGenerateReceiptNumber: e.target.checked })}
                  className="w-4 h-4 text-indigo-600 rounded"
                />
                <span className="text-xs font-bold text-gray-800 dark:text-slate-200">Auto Generate Receipt Number</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.autoCalculateFine}
                  onChange={(e) => setSettings({ ...settings, autoCalculateFine: e.target.checked })}
                  className="w-4 h-4 text-indigo-600 rounded"
                />
                <span className="text-xs font-bold text-gray-800 dark:text-slate-200">Auto Calculate Late Fee Fine</span>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-extrabold uppercase text-gray-400 dark:text-slate-400 mb-1">
              Receipt Header Note
            </label>
            <input
              type="text"
              value={settings.receiptHeaderNote}
              onChange={(e) => setSettings({ ...settings, receiptHeaderNote: e.target.value })}
              className="w-full px-3 py-2 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-xs font-medium text-gray-900 dark:text-white outline-none"
            />
          </div>

          <div>
            <label className="block text-[10px] font-extrabold uppercase text-gray-400 dark:text-slate-400 mb-1">
              Receipt Footer Note
            </label>
            <input
              type="text"
              value={settings.receiptFooterNote}
              onChange={(e) => setSettings({ ...settings, receiptFooterNote: e.target.value })}
              className="w-full px-3 py-2 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-xs font-medium text-gray-900 dark:text-white outline-none"
            />
          </div>

          <div className="pt-3 border-t border-gray-100 dark:border-slate-800 flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2.5 bg-indigo-600 text-white font-black text-xs rounded-xl hover:bg-indigo-700 transition shadow-md flex items-center gap-2"
            >
              <FaSave size={12} /> {saving ? "Saving..." : "Save System Settings"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
